import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Compact wallet-balance pill for the admin header.
 * - Reads /api/admin/wallet on mount + every 60s.
 * - Warns (amber) when below low_balance_threshold.
 * - Click → /admin/wallet (deep link to recharge).
 * - Skips polling entirely for non-admin roles (super-admin browsing /admin/*
 *   would otherwise generate noisy 403s every minute).
 */
export default function WalletBadge() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/wallet");
        if (alive) setData(data);
      } catch {
        /* swallow — endpoint may 403 in unusual contexts */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.role]);

  if (!data) return null;
  const isLow = data.is_low;
  return (
    <Link
      to="/admin/wallet"
      data-testid="wallet-badge"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs border transition-colors ${
        isLow
          ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
          : "border-[hsl(var(--kb-border))] bg-white text-[hsl(var(--kb-text))] hover:border-[hsl(var(--kb-primary))]"
      }`}
      title={isLow ? "Low balance — recharge to avoid OTP failures" : "Tenant wallet balance"}
    >
      {isLow ? <AlertTriangle size={14} /> : <Wallet size={14} />}
      <span className="font-medium" data-testid="wallet-badge-amount">
        ₹{Number(data.balance || 0).toFixed(0)}
      </span>
      {isLow && <span className="hidden sm:inline text-[10px] uppercase tracking-wider">Low</span>}
    </Link>
  );
}
